import { every, find, isEmpty, isNil, some, sum } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { useLocation } from 'react-router-dom';

import { useCustomerInfo } from '@/api/customers';
import { UpdateSubscriptionTagsConfigurationsParams } from '@/api/subscriptionTags';
import { useSubscriptionTagsTransaction } from '@/api/subscriptionTags/postSubscriptionTagsTransaction';
import {
    UpdateSubscriptionItemsParams,
    useSubscriptionsInfo,
    useUpcomingInvoice,
    useUpdateSubscriptionItems,
} from '@/api/subscriptions';
import { useCancelSubscription } from '@/api/subscriptions/cancelSubscription';
import { ProrationBehavior } from '@/common/types';
import { RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY } from '@/config';
import { BACK_TARGETS, TransactionContext } from '@/context/transaction/common';
import {
    TransactionBackTarget,
    TransactionContextProviderCommonProps,
    TransactionContextValue,
} from '@/context/transaction/types';
import { useNow } from '@/hooks/useNow';
import { combineMutationStatuses } from '@/utils/mutationHelpers';

export type SubscriptionTransactionContextProviderProps = {
    type: 'subscription';
    prorationBehavior: ProrationBehavior;
    isSubscriptionCancellation?: boolean;
} & TransactionContextProviderCommonProps;

export const SubscriptionTransactionContextProvider = ({
    totalDueLabel,
    defaultBackTarget,
    prorationBehavior,
    isSubscriptionCancellation,
    children,
}: SubscriptionTransactionContextProviderProps) => {
    const mixpanel = useMixpanel();
    const { data: customerInfo, isPending: customerPending } = useCustomerInfo();
    const location = useLocation();
    const backTarget = (location.state?.transactionBackTarget as TransactionBackTarget) ?? defaultBackTarget;
    const [overrideQuantities, setOverrideQuantities] = useState<{ [price_id: string]: number }>({});
    const [concurrentSubscriptionTagBatch, setConcurrentSubscriptionTagBatch] =
        useState<UpdateSubscriptionTagsConfigurationsParams>({});

    const { mutate: updateSubscriptionItems, ...updateSubscriptionItemsMutation } = useUpdateSubscriptionItems();
    const { mutate: cancelSubscription, ...cancelSubscriptionMutation } = useCancelSubscription();
    const { mutate: commitTagsTransaction, ...tagsTransactionMutation } = useSubscriptionTagsTransaction();

    if (isSubscriptionCancellation && prorationBehavior !== 'always_invoice') {
        throw new Error('subscriptionCancellations must have prorationBehavior=always_invoice');
    }

    const mutationStatus = useMemo(
        () =>
            combineMutationStatuses({
                updateItems: updateSubscriptionItemsMutation,
                tagsTransaction: tagsTransactionMutation,
                cancel: cancelSubscriptionMutation,
            }),
        [cancelSubscriptionMutation, updateSubscriptionItemsMutation, tagsTransactionMutation]
    );

    const { data: subscriptionInfo, isPending: subscriptionPending } = useSubscriptionsInfo({
        config: { enabled: mutationStatus.isIdle },
    });
    const subscriptionId = subscriptionInfo?.data[0]?.id;
    const hasSub = !!subscriptionInfo?.data[0];
    const isPastDue = hasSub && subscriptionInfo.data[0].status === 'past_due';

    const now = useNow(300, { enabled: !hasSub });

    const upcomingInvoiceParams = useMemo(() => {
        const config = { enabled: !subscriptionPending && mutationStatus.isIdle };

        if (isSubscriptionCancellation) {
            // if the context props say it's a cancellation, force all qtys to zero for invoice preview
            return {
                subscriptionId,
                items:
                    subscriptionInfo?.data?.[0]?.items.map(({ id }) => ({
                        id,
                        quantity: 0,
                    })) ?? [],
                config,
            };
        }
        return {
            subscriptionId,
            items: Object.entries(overrideQuantities).map(([price, quantity]) => ({
                price,
                quantity: String(quantity),
            })),
            config,
        };
    }, [
        isSubscriptionCancellation,
        mutationStatus.isIdle,
        overrideQuantities,
        subscriptionId,
        subscriptionInfo?.data,
        subscriptionPending,
    ]);
    // request two upcoming invoices, one to be resolved right now (if prorated), and one for the next
    // recurring that we use to show "next recurring invoice total". If prorationBehavior==='none' they
    // will be the same, but nbd since we're going through the query cache.
    const { data: upcomingInvoice, isFetching: invoiceUpdating } = useUpcomingInvoice({
        prorationBehavior: upcomingInvoiceParams.items.length ? prorationBehavior : undefined,
        ...upcomingInvoiceParams,
    });
    const { data: nextRecurringInvoice } = useUpcomingInvoice({
        prorationBehavior: upcomingInvoiceParams.items.length ? 'none' : undefined,
        ...upcomingInvoiceParams,
    });

    const currentSubscriptionItems = subscriptionInfo?.data[0]?.items;
    const [currentTotalDueLabel, setCurrentTotalDueLabel] = useState(
        totalDueLabel ?? (prorationBehavior === 'always_invoice' ? 'Total due now' : 'Due next invoice')
    );

    const nextInvoiceTotal = nextRecurringInvoice?.data.total;

    const hasQuantityChange = useMemo(
        () =>
            some(
                Object.entries(overrideQuantities).map(([priceId, newQty]) => {
                    const existingItem = subscriptionInfo?.data[0]?.items.find(({ price_id }) => price_id === priceId);
                    return (existingItem?.quantity ?? 0) !== newQty;
                })
            ),
        [overrideQuantities, subscriptionInfo?.data]
    );

    const isCancellation = useMemo(() => {
        // If the caller asked for a cancellation, it's a cancellation.
        if (isSubscriptionCancellation) {
            return true;
        }

        if (subscriptionInfo?.data.length === 0) {
            // if we have no subscription, this couldn't possibly be a cancellation. new subs do sometimes briefly look like they have no items
            // while we're composing the initial sub, so short-circuit here to avoid flashing a red "you're about to cancel" alert while loading
            return false;
        }

        /*
         * We cancel the subscription if every newly overridden quantity is zero, and every item in the current subscription
         * is overridden, *except* recurring sat credits, which we'll cancel if they zeroed out everything else.
         */
        return (
            every(overrideQuantities, (q) => q === 0) &&
            every(
                subscriptionInfo?.data[0]?.items,
                ({ price_id, price_lookup_key }) =>
                    price_lookup_key === RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY || price_id in overrideQuantities
            )
        );
    }, [isSubscriptionCancellation, overrideQuantities, subscriptionInfo?.data]);

    const updateSubscription = useCallback(() => {
        if (subscriptionPending || invoiceUpdating) {
            throw new Error('Unable to update pending subscription');
        }
        if (isCancellation) {
            if (!subscriptionId) {
                throw new Error('Unable to cancel subscription without id');
            }
            cancelSubscription(subscriptionId);
            return;
        }
        const items: UpdateSubscriptionItemsParams['items'] =
            currentSubscriptionItems?.map(({ id, price_id, quantity }) => ({
                id,
                quantity: overrideQuantities[price_id] ?? quantity,
            })) ?? [];
        Object.entries(overrideQuantities).forEach(([price_id, quantity]) => {
            const currentItem = find(currentSubscriptionItems, { price_id });
            if (!currentItem && quantity > 0) {
                items.push({ price: price_id, quantity });
            }
        });
        if (prorationBehavior === 'none') {
            // commitTagsTransaction only prorates, so in this case, just use standard update
            if (!subscriptionId) {
                throw new Error('Unable to update subscription without id');
            }
            if (!isEmpty(concurrentSubscriptionTagBatch)) {
                throw new Error('Unable to update subscription tags without proration');
            }
            updateSubscriptionItems({ items, prorationBehavior, subscriptionId });
            return;
        }
        commitTagsTransaction({ subscriptionTagChanges: concurrentSubscriptionTagBatch, subscriptionItems: items });
    }, [
        cancelSubscription,
        commitTagsTransaction,
        concurrentSubscriptionTagBatch,
        currentSubscriptionItems,
        invoiceUpdating,
        isCancellation,
        overrideQuantities,
        prorationBehavior,
        subscriptionId,
        subscriptionPending,
        updateSubscriptionItems,
    ]);

    const totalDue = useMemo(() => {
        if (isPastDue) {
            if (isCancellation) {
                // in this case, upcoming invoices are untrustworthy -- might show a refund for the current month, despite
                // us not having received payment for it.
                return 0;
            }
            // if we're past due, the only subscription change allowed is cancellation. anything else should be hard to get to
            // in the UI, but don't claim any particular total if it leaks out anywhere, and this should also prevent confirming
            // any transactions.
            return undefined;
        }
        if (prorationBehavior === 'none') {
            return sum(
                upcomingInvoice?.data.lines
                    .filter((l) => Object.keys(overrideQuantities).includes(l.price_id))
                    .map((l) => l.amount)
            );
        }
        if (!hasQuantityChange) {
            return 0;
        }
        if (!upcomingInvoice) {
            return undefined;
        }

        if (isCancellation) {
            // this one is weird: if we're canceling, we don't want to prorate "unused" sat credits since they've already been credited,
            // so we need to subtract off their amount. the backend will remove the item with no proration before doing the prorated cancelation.
            const satCreditPriceId = find(subscriptionInfo?.data[0]?.items, {
                price_lookup_key: RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY,
            })?.price_id;
            const satCreditItem =
                !!satCreditPriceId && find(upcomingInvoice.data.lines, { price_id: satCreditPriceId });
            if (!satCreditItem) {
                return upcomingInvoice.data.total;
            }
            return upcomingInvoice.data.total - satCreditItem.amount;
        }
        return upcomingInvoice.data.total;
    }, [
        hasQuantityChange,
        isCancellation,
        isPastDue,
        overrideQuantities,
        prorationBehavior,
        subscriptionInfo?.data,
        upcomingInvoice,
    ]);

    const hasPayment = useMemo(() => {
        if (isCancellation) {
            // don't lock out cancellation for lack of payment (and in some cases upcomingInvoice amount_due may not be trustworthy)
            return true;
        }
        if (prorationBehavior === 'none') {
            // if we're prorating, just let them do it because nothing is due now. in this case, upcomingInvoice
            // is the next regular payment. even if they don't have the balance to cover that one, we'll still
            // let them proceed with this transaction and then rely on notifications to get them to give us a method.
            return true;
        }
        if (!hasQuantityChange) {
            // if we have no quantity change, upcomingInvoice reflects the next regular payment, so the amount due
            // doesn't actually reflect payment we need to collect right now. we can let this txn proceed even without
            // a payment method because it's just a telemetry reassignment.
            return true;
        }
        if (upcomingInvoice && upcomingInvoice.data.amount_due <= 0) {
            return true;
        }

        return !!customerInfo?.data.default_payment_method;
    }, [
        isCancellation,
        prorationBehavior,
        hasQuantityChange,
        upcomingInvoice,
        customerInfo?.data.default_payment_method,
    ]);

    const contextValue: TransactionContextValue = useMemo(() => {
        const commonContext = {
            type: 'subscription',
            backTarget: BACK_TARGETS[backTarget],
        } as const;

        if (customerPending || subscriptionPending) {
            return {
                ...commonContext,
                totalDueLabel: currentTotalDueLabel,
                isPending: true,
                isUpdating: true,
                hasChanges: false,
                hasPayment: false,
                track: (eventName: string, properties?: object) =>
                    mixpanel?.track(eventName, {
                        type: 'subscription',
                        prorationBehavior,
                        hasPayment,
                        hasChanges: false,
                        isPending: true,
                        ...properties,
                    }),
            };
        }

        /**
         * Only return proratedLineItems if we're prorating *and* we have a quantity change. If there's no quantity change, Stripe gives us
         * the next subscription invoice as upcomingInvoice, and there isn't a great way to tell the difference.
         */
        const proratedLineItems =
            prorationBehavior === 'always_invoice' && hasQuantityChange && !isPastDue
                ? upcomingInvoice?.data?.lines
                : [];

        const billingDate = hasSub ? new Date(subscriptionInfo.data[0].current_period_end * 1000) : now;

        const isNewSubscription = subscriptionInfo?.data?.length === 0;

        const hasChanges =
            hasQuantityChange || !isEmpty(concurrentSubscriptionTagBatch) || Boolean(subscriptionId && isCancellation);

        return {
            ...commonContext,
            totalDueLabel: currentTotalDueLabel,
            totalDue,
            paymentDue: prorationBehavior === 'none' || isPastDue ? undefined : upcomingInvoice?.data.amount_due,
            isPending: false,
            isUpdating: invoiceUpdating,
            prorationBehavior,
            currency: currentSubscriptionItems?.[0]?.currency ?? customerInfo?.data.currency,
            nextInvoiceTotal,
            billingDate,
            lineItemQuantities: overrideQuantities,
            setLineItemQuantity(price_id, quantity) {
                setOverrideQuantities((quantities) => {
                    if (quantities[price_id] === quantity) {
                        return quantities;
                    }
                    return { ...quantities, [price_id]: quantity };
                });
            },
            setConcurrentSubscriptionTagBatch,
            hasChanges,
            hasPayment,
            proratedLineItems,
            isCancellation,
            isNewSubscription,
            isPastDue,
            updateSubscription,
            mutationStatus,
            track: (eventName: string, properties?: object) =>
                mixpanel?.track(eventName, {
                    type: 'subscription',
                    prorationBehavior,
                    hasPayment,
                    hasChanges,
                    isPending: false,
                    isCancellation,
                    totalDue,
                    nextInvoiceTotal,
                    billingDate,
                    mutationStatus: mutationStatus.status,
                    ...properties,
                }),
        };
    }, [
        backTarget,
        customerPending,
        subscriptionPending,
        prorationBehavior,
        hasQuantityChange,
        isPastDue,
        upcomingInvoice?.data?.lines,
        upcomingInvoice?.data.amount_due,
        hasSub,
        subscriptionInfo?.data,
        now,
        concurrentSubscriptionTagBatch,
        subscriptionId,
        isCancellation,
        currentTotalDueLabel,
        totalDue,
        invoiceUpdating,
        currentSubscriptionItems,
        customerInfo?.data.currency,
        nextInvoiceTotal,
        overrideQuantities,
        hasPayment,
        updateSubscription,
        mutationStatus,
        mixpanel,
    ]);

    /**
     * Effect: If we're invoicing now and we're controlling the totalDueLabel, keep it updated for "due" vs. "refund".
     *
     * It's useState via useEffect rather than just memoized in the main value so that we can smooth over loading times with the last value.
     */
    useEffect(() => {
        if (totalDueLabel) {
            // prop always wins if present
            return;
        }
        if (contextValue.isPending) {
            return;
        }
        if (prorationBehavior === 'always_invoice' && !isNil(contextValue.totalDue)) {
            if (contextValue.totalDue >= 0) {
                setCurrentTotalDueLabel('Total due now');
            } else {
                setCurrentTotalDueLabel('Total credit to refund');
            }
        }
    }, [contextValue, prorationBehavior, totalDueLabel]);

    return <TransactionContext.Provider value={contextValue}>{children}</TransactionContext.Provider>;
};
